import React from 'react';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { H3, SubHeaderDiv } from '@ui-library/typography';
import { isValidationLoadingType } from '@components/sources/source/addSource';
import DocLinkLabel from '@components/sources/docLinksLabel';
import { get } from 'lodash';
import { FixMe } from '@utils/types';
import { CreateAccountButton } from '@components/sources/source/styles';
import WarehouseSettings from '../warehouseSettings';
import AuthAccounts from './authAccounts';
import { warehouseList } from '../warehouseSourceList/warehouseSettings';
import classes from '../common.module.scss';

interface IWarehouseAuthProps {
  sourceDefinition: ISourceDefinition | undefined;
  onChange: (accountId: string, accountInfo: FixMe, isValid: boolean) => void;
  initialSettings?: FixMe;
  disabled?: boolean;
  mode?: 'account' | 'form';
  selectedAccount?: string;
  checkValid?: (val: isValidationLoadingType) => void;
  isValidationLoading?: isValidationLoadingType;
  isValidationDisable?: boolean;
  alterValidation?: () => void;
}

interface IWarehouseAuthState {
  mode?: 'account' | 'form';
  hasAccounts?: boolean;
  modeChanged?: boolean;
}

class DataWarehouseAuth extends React.Component<IWarehouseAuthProps, IWarehouseAuthState> {
  constructor(props: IWarehouseAuthProps) {
    super(props);
    this.state = {
      mode: props.mode || 'account',
      modeChanged: false,
    };
  }

  handleOnCheckValid = () => {
    if (this.props.checkValid) {
      this.props.checkValid(isValidationLoadingType.hide);
    }
  };

  handleOnClick = () => {
    const { onChange } = this.props;
    const { mode } = this.state;
    if (mode === 'form') {
      this.setState({ mode: 'account', modeChanged: true });
    } else {
      this.setState({ mode: 'form', modeChanged: true });
    }
    onChange('', undefined, false);
    this.handleOnCheckValid();
  };

  public checkAccountValidation = (accountId: string, accountInfo: FixMe, isValid: boolean) => {
    const { checkValid, alterValidation, onChange, isValidationDisable, isValidationLoading } =
      this.props;
    if (
      isValidationDisable !== true &&
      checkValid &&
      alterValidation &&
      isValidationLoading === isValidationLoadingType.disable
    ) {
      alterValidation();
    }

    onChange(accountId, accountInfo, isValid);
  };

  static getDerivedStateFromProps(props: FixMe, state: FixMe) {
    const { mode, modeChanged, hasAccounts } = state;
    let authMode = mode;
    if (!modeChanged && hasAccounts === false) {
      authMode = 'form';
    }
    return {
      mode: authMode,
    };
  }

  getDocLinkLabel = () => {
    /* The function returns the doc-link component based on the condition of the source
     supports incremental sync or not. For incremental sync sources, it provides the
     Grant Permissions component or else the whole Setup Instruction component. */
    const { sourceDefinition, isValidationDisable } = this.props;
    if (!sourceDefinition) {
      return null;
    }
    if (sourceDefinition.options?.syncBehaviours?.length && !isValidationDisable) {
      const grantPermissionLink = get(warehouseList, sourceDefinition.name.toUpperCase(), [{}])[0]
        ?.docLinks?.grantPermissions;
      return (
        <>
          <SubHeaderDiv className={classes.division_title}>Grant Permissions</SubHeaderDiv>
          <DocLinkLabel
            description={`RudderStack requires specific permissions to sync data. You 
          can find detailed ${sourceDefinition.displayName} instructions`}
            linkRef={grantPermissionLink}
          />
        </>
      );
    }

    const setupInstructionsLink = get(warehouseList, sourceDefinition.name.toUpperCase(), [{}])[0]
      ?.docLinks?.setupInstructions;
    return (
      <>
        <SubHeaderDiv className={classes.division_title}>Setup Instructions</SubHeaderDiv>
        <DocLinkLabel
          description={`
      Follow our step-by-step guide to setup ${sourceDefinition.displayName} as a Source in RudderStack here:`}
          linkRef={setupInstructionsLink}
          linkText={`${sourceDefinition.displayName} integration guide`}
        />
      </>
    );
  };

  public render() {
    const { sourceDefinition, selectedAccount, isValidationLoading, isValidationDisable } =
      this.props;
    const { mode, hasAccounts } = this.state;
    return (
      <div className={classes.grant_permissions_wrapper} data-testid="warehouse-auth">
        {this.getDocLinkLabel()}
        {(mode === 'account' && (
          <AuthAccounts
            hasAccounts={(hasAccounts) => this.setState({ hasAccounts })}
            selectedAccount={selectedAccount}
            role={sourceDefinition?.name || ''}
            onClick={(accountId, accountInfo) => {
              this.checkAccountValidation(accountId, accountInfo, !!accountId);
            }}
            isValidationLoading={isValidationLoading}
            isValidationDisable={isValidationDisable}
          />
        )) || (
          <WarehouseSettings
            onSettingsChange={(accountId, accountInfo, isValid) => {
              this.checkAccountValidation(accountId, accountInfo, isValid);
            }}
            sourceDefinition={sourceDefinition}
            disabled={isValidationLoading === isValidationLoadingType.loading}
          >
            <div className="m-b-lg">
              <H3>Connection Credentials</H3>
            </div>
          </WarehouseSettings>
        )}
        {hasAccounts === true && (
          <CreateAccountButton
            secondary
            disabled={isValidationLoading === 'loading' && !isValidationDisable}
            onClick={this.handleOnClick}
          >
            {mode === 'form' ? 'Use an existing account' : 'Create Credentials from Scratch'}
          </CreateAccountButton>
        )}
      </div>
    );
  }
}

export default DataWarehouseAuth;
